import type { PlatformConfig, PlatformId } from "./configs";
import { PLATFORM_CONFIGS } from "./configs";

/**
 * Format a transparent-background canvas for a specific platform.
 * Returns a new canvas with the correct dimensions and background.
 */
export function formatForPlatform(
	foreground: HTMLCanvasElement,
	platformId: PlatformId,
): HTMLCanvasElement {
	const config = PLATFORM_CONFIGS[platformId];

	if (platformId === "transparent") {
		return foreground;
	}

	return renderWithConfig(foreground, config);
}

function renderWithConfig(
	foreground: HTMLCanvasElement,
	config: PlatformConfig,
): HTMLCanvasElement {
	const canvas = document.createElement("canvas");
	canvas.width = config.width;
	canvas.height = config.height;
	const ctx = canvas.getContext("2d")!;

	// Fill background
	if (config.bgColor !== "transparent") {
		ctx.fillStyle = config.bgColor;
		ctx.fillRect(0, 0, config.width, config.height);
	}

	const availW = config.width - config.padding * 2;
	const availH = config.height - config.padding * 2;

	// Scale to fit within available area, maintaining aspect ratio
	const srcW = foreground.width;
	const srcH = foreground.height;
	const scale = Math.min(availW / srcW, availH / srcH);

	// For Amazon: ensure 85% coverage
	let finalScale = scale;
	if (config.minCoverage) {
		const coverageScale = Math.sqrt(config.minCoverage);
		const minScale = Math.min(availW / srcW, availH / srcH) * coverageScale;
		finalScale = Math.max(scale, minScale);
		// Clamp so it doesn't exceed available area too much
		finalScale = Math.min(finalScale, Math.min(availW / srcW, availH / srcH));
	}

	const drawW = Math.round(srcW * finalScale);
	const drawH = Math.round(srcH * finalScale);
	const drawX = Math.round((config.width - drawW) / 2);
	const drawY = Math.round((config.height - drawH) / 2);

	ctx.drawImage(foreground, drawX, drawY, drawW, drawH);

	return canvas;
}

/**
 * Convert canvas to Blob (PNG for transparent, JPEG for white-bg)
 */
export function canvasToBlob(
	canvas: HTMLCanvasElement,
	transparent = false,
): Promise<Blob> {
	return new Promise((resolve, reject) => {
		const mimeType = transparent ? "image/png" : "image/jpeg";
		const quality = transparent ? undefined : 0.92;
		canvas.toBlob(
			(blob) => {
				if (blob) resolve(blob);
				else reject(new Error("Failed to convert canvas to blob"));
			},
			mimeType,
			quality,
		);
	});
}

/**
 * Apply low-resolution + watermark for free tier
 */
export function applyFreeTierRestriction(
	canvas: HTMLCanvasElement,
): HTMLCanvasElement {
	const MAX_SIZE = 512;
	const scale = Math.min(1, MAX_SIZE / Math.max(canvas.width, canvas.height));

	const result = document.createElement("canvas");
	result.width = Math.round(canvas.width * scale);
	result.height = Math.round(canvas.height * scale);
	const ctx = result.getContext("2d")!;

	ctx.drawImage(canvas, 0, 0, result.width, result.height);

	// Watermark
	const fontSize = Math.max(14, Math.round(result.width * 0.05));
	ctx.font = `bold ${fontSize}px sans-serif`;
	ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
	ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
	ctx.lineWidth = 2;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";

	const text = "BG Removal - Trial";
	const cx = result.width / 2;
	const cy = result.height / 2;

	ctx.save();
	ctx.translate(cx, cy);
	ctx.rotate(-Math.PI / 6);
	ctx.strokeText(text, 0, 0);
	ctx.fillText(text, 0, 0);
	ctx.restore();

	return result;
}
